import React, { useState } from 'react';

/**
 * 排休協調（店長端彙整頁內的一個區塊）。
 *
 * ⛔ 這裡 ⛔ 不列「同一天有多人想休」，也 ⛔ 不列「員工劃了假但還沒定案」。
 *    只列後端 LeaveCoordinationService 判定「無法依既有內規自動採納」的那幾筆，
 *    而且每一筆都寫出具體數字的原因。
 *
 * ⚠️ 2026-09-22 起「已自動採納」與「你做過的決定」**併進這同一張卡**，用按鈕展開
 *    （原本是各自一張卡 ＋ 各自一段說明，佳儀回饋「卡片太多、小字太多」）。
 *    ⛔ 資料與操作一項都沒有拿掉，只是預設收起來。
 */

const dayLabel = (item) => `${item.date.slice(5).replace('-', '/')}`;

const META_LINE = (item) =>
    [
        item.rank_label || '順位待排',
        item.requested_at ? `提出 ${item.requested_at}` : null,
        `本月已劃 ${item.used_days} 天`,
        item.scenario_label,
    ]
        .filter(Boolean)
        .join('　·　');

/** 已自動採納：只有日期與姓名，⛔ 沒有說明文字（規則寫在「說明」裡講一次） */
const AutoList = ({ auto }) => {
    const byDate = {};
    auto.forEach((a) => {
        (byDate[a.date] = byDate[a.date] || []).push(a);
    });

    return (
        <div className="sc-card__body sc-card__body--tight" data-panel="auto">
            <ul className="sc-coord__auto">
                {Object.keys(byDate)
                    .sort()
                    .map((date) => (
                        <li key={date}>
                            <b>{date.slice(5).replace('-', '/')}</b>
                            <span>{byDate[date].map((a) => `${a.name}（${a.period_label}）`).join('、')}</span>
                        </li>
                    ))}
            </ul>
        </div>
    );
};

const DecidedTable = ({ decided, busy, onClear }) => (
    <table className="sc-table" data-panel="decided">
        <thead>
            <tr>
                <th style={{ width: '90px' }}>日期</th>
                <th style={{ width: '120px' }}>姓名</th>
                <th style={{ width: '170px' }}>決定</th>
                <th>紀錄</th>
                <th style={{ width: '110px' }} />
            </tr>
        </thead>
        <tbody>
            {decided.map((d) => (
                <tr key={`${d.uuid}|${d.date}`}>
                    <td className="is-nowrap">{d.date.slice(5).replace('-', '/')}</td>
                    <td>{d.name}</td>
                    <td>
                        <span
                            className={`sc-badge ${d.state === 'agreed' ? 'sc-badge--green' : 'sc-badge--grey'}`}
                        >
                            {d.state_label}
                        </span>
                        {d.state === 'agreed' && d.period_label ? `　一般休・${d.period_label}` : ''}
                    </td>
                    <td style={{ color: 'var(--sc-ink-2)' }}>
                        {[d.decided_by, d.decided_at].filter(Boolean).join('　·　') || '—'}
                    </td>
                    <td>
                        <button
                            type="button"
                            className="sc-btn sc-btn--xs"
                            disabled={busy}
                            data-clear-decision={`${d.uuid}|${d.date}`}
                            onClick={() => onClear(d)}
                        >
                            取消決定
                        </button>
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const PendingRow = ({ item, busy, onDecide, periodOptions }) => {
    const [period, setPeriod] = useState(item.period);

    return (
        <tr data-coord-row={`${item.uuid}|${item.date}`}>
            <td className="is-nowrap">
                <b>{dayLabel(item)}</b>
            </td>
            <td className="is-nowrap">
                {item.name}
                <span className="sc-sub">{META_LINE(item)}</span>
            </td>
            <td>
                <span className="sc-badge sc-badge--orange">{item.title}</span>
                <span className="sc-coord__why">{(item.reasons || []).join('；')}</span>
                {item.rivals && item.rivals.length > 1 && (
                    <span className="sc-sub">同日一起比的：{item.rivals.join('、')}</span>
                )}
            </td>
            <td>
                <div className="sc-btns">
                    <select
                        className="sc-input sc-input--xs sc-input--auto"
                        aria-label="同意的時段"
                        value={period}
                        disabled={busy}
                        onChange={(e) => setPeriod(e.target.value)}
                    >
                        {periodOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="sc-btn sc-btn--xs sc-btn--primary"
                        disabled={busy}
                        data-agree={`${item.uuid}|${item.date}`}
                        onClick={() => onDecide(item, 'agreed', period)}
                    >
                        同意休假
                    </button>
                    <button
                        type="button"
                        className="sc-btn sc-btn--xs"
                        disabled={busy}
                        data-keep={`${item.uuid}|${item.date}`}
                        onClick={() => onDecide(item, 'kept_work', null)}
                    >
                        維持上班
                    </button>
                </div>
            </td>
        </tr>
    );
};

const CoordinationPending = ({ coordination, busy, onDecide, onClear }) => {
    // 'auto' | 'decided' | 'help' | null —— 一次只開一個，⛔ 不預設展開任何一個
    const [open, setOpen] = useState(null);

    if (!coordination) return null;

    const pending = coordination.pending || [];
    const auto = coordination.auto || [];
    const decided = coordination.decided || [];
    const periodOptions = coordination.period_options || [];
    const toggle = (key) => setOpen((prev) => (prev === key ? null : key));

    return (
        <section className={`sc-card ${pending.length ? 'sc-card--accent-orange' : ''}`}>
            <div className="sc-card__head">
                <h2 className="sc-card__title" style={pending.length ? { color: 'var(--sc-orange)' } : undefined}>
                    排休協調
                    <span
                        className={`sc-badge ${pending.length ? 'sc-badge--orange' : 'sc-badge--green'}`}
                        data-coord-pending-badge
                    >
                        待決定 {pending.length} 筆
                    </span>
                </h2>
                <div className="sc-card__act">
                    <button
                        type="button"
                        className={`sc-btn sc-btn--xs${open === 'auto' ? ' is-on' : ''}`}
                        data-toggle="auto"
                        onClick={() => toggle('auto')}
                    >
                        已自動採納 {auto.length}
                    </button>
                    {decided.length > 0 && (
                        <button
                            type="button"
                            className={`sc-btn sc-btn--xs${open === 'decided' ? ' is-on' : ''}`}
                            data-toggle="decided"
                            onClick={() => toggle('decided')}
                        >
                            我的決定 {decided.length}
                        </button>
                    )}
                    <button
                        type="button"
                        className={`sc-btn sc-btn--xs${open === 'help' ? ' is-on' : ''}`}
                        data-toggle="help"
                        onClick={() => toggle('help')}
                    >
                        說明
                    </button>
                </div>
            </div>

            {/* 規則說明：⛔ 不常駐。內容是原本鋪在卡片上、表格上下各一段的那幾句。 */}
            {open === 'help' && (
                <div className="sc-card__body sc-card__body--tight" data-panel="help">
                    <p className="sc-help" style={{ margin: 0 }}>
                        「同一天有多人想休」本身不算衝突 —— 只要最低在班人力、可休名額與每月可休天數
                        都過得了，系統就直接採納（＝「已自動採納」，格子右上角有綠點）。
                        下面列的是真的過不了的，每一筆寫出是哪個時段、哪個職能、差幾個人。
                        {coordination.sort_hint ? `　${coordination.sort_hint}。` : ' '}
                        決定會立刻寫回班表與每日人力並留下異動紀錄；「維持上班」不會刪掉當事人的劃假，
                        本人仍可於發布後走請假流程提出異動。
                    </p>
                </div>
            )}

            {pending.length === 0 ? (
                /* 一行就夠：⛔ 不用整張 sc-empty 佔一整塊高度去講「沒事」 */
                <div className="sc-card__body sc-card__body--tight" data-coord-empty>
                    <span className="sc-muted">沒有要你決定的排休</span>
                </div>
            ) : (
                <table className="sc-table">
                    <thead>
                        <tr>
                            <th style={{ width: '70px' }}>日期</th>
                            <th style={{ width: '210px' }}>誰想休</th>
                            <th>為什麼需要你決定</th>
                            <th style={{ width: '270px' }}>決定</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pending.map((item) => (
                            <PendingRow
                                key={`${item.uuid}|${item.date}`}
                                item={item}
                                busy={busy}
                                onDecide={onDecide}
                                periodOptions={periodOptions}
                            />
                        ))}
                    </tbody>
                </table>
            )}

            {open === 'decided' && decided.length > 0 && (
                <DecidedTable decided={decided} busy={busy} onClear={onClear} />
            )}
            {open === 'auto' &&
                (auto.length ? (
                    <AutoList auto={auto} />
                ) : (
                    <div className="sc-empty" data-panel="auto">
                        —
                    </div>
                ))}
        </section>
    );
};

export default CoordinationPending;
